import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { memo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";

import { createUserSchema, UserFormData } from "../model/schemas";

import { UserForm } from "./UserForm";

import { useCreateUser, CreateUserDTO, CreateUserDTORole, getCompany } from "@/common/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, Button } from "@/common/components";
import { useToast } from "@/common/hooks";

interface CreateUserDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

// Преобразование данных формы в формат API
export const CreateUserDialog = memo(function CreateUserDialog({
  isOpen,
  onOpenChange,
}: CreateUserDialogProps) {
  const { toast } = useToast();

  const createForm = useForm<UserFormData>({
    resolver: zodResolver(createUserSchema),
    mode: "onChange",
    defaultValues: {
      username: "",
      login: "",
      passwordHash: "",
      role: CreateUserDTORole.CARGO_OWNER,
      companyName: "",
      inn: "",
      kpp: "",
      ogrn: "",
      companyEmail: "",
      contactNumber: "",
      companyContactNumber: "",
      addressLegal: "",
      addressActual: "",
    },
  });

  // Мутация для создания пользователя
  const { mutateAsync: createUserMutate, isPending: isCreating } = useCreateUser();

  // Мутация для поиска компании по ИНН
  const { mutateAsync: searchCompanyByInn, isPending: isSearchingCompany } = useMutation({
    mutationFn: (inn: string) => getCompany({ inn }),
  });

  // Сброс формы при открытии диалога
  useEffect(() => {
    if (isOpen) {
      createForm.reset();
    }
  }, [isOpen, createForm]);

  const handleSearchCompany = useCallback(async () => {
    const innValue = createForm.getValues("inn");
    if (!innValue || !/^\d{10}$|^\d{12}$/.test(innValue)) {
      toast({
        variant: "destructive",
        title: "Ошибка",
        description: "Введите корректный ИНН (10 или 12 цифр)",
      });
      return;
    }

    try {
      const response = await searchCompanyByInn(innValue);
      if (response.data.payload) {
        const companyData = response.data.payload;
        const fieldsToUpdate: (keyof UserFormData)[] = [];
        
        // Опции для setValue: не запускать валидацию и не помечать поля как тронутые
        const setValueOptions = { shouldValidate: false, shouldTouch: false };
        
        if (companyData.companyName) {
          createForm.setValue("companyName", companyData.companyName, setValueOptions);
          fieldsToUpdate.push("companyName");
        }
        if (companyData.inn) {
          createForm.setValue("inn", companyData.inn, setValueOptions);
          fieldsToUpdate.push("inn");
        }
        if (companyData.kpp) {
          createForm.setValue("kpp", companyData.kpp, setValueOptions);
          fieldsToUpdate.push("kpp");
        }
        if (companyData.ogrn) {
          createForm.setValue("ogrn", companyData.ogrn, setValueOptions);
          fieldsToUpdate.push("ogrn");
        }
        if (companyData.companyEmail) {
          createForm.setValue("companyEmail", companyData.companyEmail, setValueOptions);
          fieldsToUpdate.push("companyEmail");
        }
        if (companyData.contactNumber) {
          createForm.setValue("companyContactNumber", companyData.contactNumber, setValueOptions);
          fieldsToUpdate.push("companyContactNumber");
        }
        if (companyData.addressLegal) {
          createForm.setValue("addressLegal", companyData.addressLegal, setValueOptions);
          fieldsToUpdate.push("addressLegal");
        }
        if (companyData.addressActual) {
          createForm.setValue("addressActual", companyData.addressActual, setValueOptions);
          fieldsToUpdate.push("addressActual");
        }
        
        // Очищаем ошибки валидации для заполненных полей
        if (fieldsToUpdate.length > 0) {
          createForm.clearErrors(fieldsToUpdate);
        }
        
        toast({
          title: "Успешно",
          description: "Данные компании загружены",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Ошибка",
        description: error instanceof Error ? error.message : "Не удалось найти компанию по ИНН",
      });
    }
  }, [createForm, searchCompanyByInn, toast]);

  const onCreateSubmit = useCallback(
    (data: UserFormData) => {
      const { passwordHash, ...rest } = data;
      createUserMutate({
        data: {
          ...rest,
          passwordHash: passwordHash ?? "",
          email: data.email || undefined,
          companyEmail: data.companyEmail || undefined,
          kpp: data.kpp || "",
          ogrn: data.ogrn || "",
          contactNumber: data.contactNumber || "",
          companyContactNumber: data.companyContactNumber || "",
        } satisfies CreateUserDTO,
      })
        .then(() => {
          onOpenChange(false);
          createForm.reset();
          toast({
            title: "Успешно",
            description: "Пользователь создан",
          });
        })
        .catch((error: unknown) => {
          toast({
            variant: "destructive",
            title: "Ошибка создания пользователя",
            description: error instanceof Error ? error.message : "Не удалось создать пользователя",
          });
        });
    },
    [createUserMutate, onOpenChange, createForm, toast],
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="btn-primary">
          <UserPlus className="w-4 h-4 mr-2" />
          Создать пользователя
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Создание нового пользователя</DialogTitle>
        </DialogHeader>

        <UserForm
          mode="create"
          form={createForm}
          onSubmit={onCreateSubmit}
          isSubmitting={isCreating}
          submitLabel="Создать"
          submittingLabel="Создание..."
          onCancel={() => onOpenChange(false)}
          onSearchCompany={handleSearchCompany}
          isSearchingCompany={isSearchingCompany}
        />
      </DialogContent>
    </Dialog>
  );
});
